/**
 * Pure edit operations — the mutation recipes that used to live inside
 * `useTimeline`'s action closures (docs/agent.md §3).
 *
 * Contract every op keeps:
 *  - operates in place on an immer draft of an Edit; never reads Zustand, the
 *    Y.Doc, or any module state. Everything contextual (playhead, target track,
 *    clipboard payload) arrives as args.
 *  - args are a single JSON-serializable object, because the agent's op log is
 *    persisted into the CRDT and replayed later (§5.2).
 *  - returns `Produced` — the ids it created, in deterministic order. Replay
 *    zips these against the originals to remap id-typed args.
 *  - does NOT normalize. The caller owns that: the store's `apply` normalizes
 *    once per action, and replay normalizes once per replayed op, which is the
 *    same granularity.
 *
 * Ops are permissive about missing targets (no track of the kind, unknown clip
 * id): they return `{ created: [] }` and leave the draft untouched, matching the
 * store's long-standing silent-no-op behavior. The agent tool layer (§7) is what
 * turns "nothing happened" into an instructive error for the model.
 */
import { clipDuration, clipEnd, genId, isAudioClip, isOverlayClip, reassignEffectIds } from './helpers';
import {
  moveClipToTrack as moveClipToTrackInvariant,
  removeTransition,
  rippleLaterClips,
  setTransition,
  splitAudioClip,
  splitVideoClip,
  trackHasRoom,
} from './invariants';
import {
  clipParamTime,
  getParamAtPath,
  isAnimated,
  removeKeyframeAt,
  sampleParam,
  setParamAt,
  setParamAtPath,
} from './params';
import { findFreeOverlayStart, overlayIntervals } from './textInsert';
import type {
  AnyClip,
  AudioClip,
  AudioTrack,
  Edit,
  EditSettings,
  PandoraVideoSource,
  Source,
  TextClip,
  TextStyle,
  Track,
  TrackBlend,
  TransitionType,
  VideoClip,
} from './types';

/** What an op created. `created` is ordered; replay depends on that order. */
export interface Produced {
  created: string[];
}

const NOTHING: Produced = { created: [] };

export const DEFAULT_TEXT_STYLE: TextStyle = {
  fontFamily: 'sans',
  fontSize: 0.06,
  color: '#ffffff',
  bold: true,
  italic: false,
  align: 'center',
  background: '',
  outline: '#000000',
  outlineWidth: 0.003,
};

// ---------------------------------------------------------------------------
// shared lookups
// ---------------------------------------------------------------------------

export interface ClipLocation {
  trackIndex: number;
  track: Track;
  clip: AnyClip;
}

export function findClip(edit: Edit, clipId: string): ClipLocation | null {
  for (let i = 0; i < edit.tracks.length; i++) {
    const track = edit.tracks[i];
    const clip = (track.clips as AnyClip[]).find((c) => c.id === clipId);
    if (clip) return { trackIndex: i, track, clip };
  }
  return null;
}

export function firstTrackOfKind(edit: Edit, kind: Track['kind']): Track | null {
  return edit.tracks.find((t) => t.kind === kind) ?? null;
}

/**
 * Resolve the track new clips of a kind land on: the explicitly targeted track
 * when it exists and matches the kind, else the first track of that kind.
 * `target` is the caller's ephemeral per-kind preference (the store's
 * `targetTrack`), passed in rather than read.
 */
export function resolveTargetTrack(
  edit: Edit,
  kind: Track['kind'],
  target: Partial<Record<Track['kind'], string>> = {},
): Track | null {
  const targetId = target[kind];
  if (targetId) {
    const t = edit.tracks.find((tt) => tt.id === targetId && tt.kind === kind);
    if (t) return t;
  }
  return firstTrackOfKind(edit, kind);
}

/** Add (or reuse) a pandora source in the edit's sources map; returns its id. */
export function ensureSource(edit: Edit, source: PandoraVideoSource): string {
  for (const [id, s] of Object.entries(edit.sources)) {
    if (
      s.kind === 'pandora-video' &&
      s.archive === source.archive &&
      s.item === source.item &&
      s.part === source.part
    )
      return id;
  }
  const id = genId('src_');
  edit.sources[id] = { ...source, url: null };
  return id;
}

/**
 * Ensure any Source (pandora OR image) exists in the edit; returns its id.
 * Pandora sources dedupe by archive/item/part (via ensureSource); image sources
 * dedupe by url. Used by paste so clips copied from another edit keep working.
 */
export function ensureAnySource(edit: Edit, source: Source): string {
  if (source.kind === 'pandora-video') return ensureSource(edit, source);
  for (const [id, s] of Object.entries(edit.sources)) {
    if (s.kind === 'image' && s.url === source.url) return id;
  }
  const id = genId('src_');
  edit.sources[id] = { ...source };
  return id;
}

/** Timeline end of a track's last clip (0 for an empty track). */
function trackEnd(track: Track): number {
  return (track.clips as AnyClip[]).reduce((m, c) => Math.max(m, clipEnd(c)), 0);
}

function newVideoClip(id: string, srcId: string, inPoint: number, outPoint: number, start: number): VideoClip {
  return {
    id,
    source: srcId,
    in: inPoint,
    out: outPoint,
    start,
    speed: 1,
    volume: 1,
    muted: false,
    fadeIn: 0,
    fadeOut: 0,
    effects: [],
    transform: { scale: 1, x: 0, y: 0, rotate: 0, opacity: 1 },
    transitionIn: null,
  };
}

// ---------------------------------------------------------------------------
// clip ops
// ---------------------------------------------------------------------------

export interface AddVideoClipArgs {
  source: PandoraVideoSource;
  in: number;
  out: number;
  /** Requested timeline start. Honored only when it is past the track's end;
   *  otherwise the clip appends (the store's long-standing behavior). */
  at?: number;
  targetTrack?: Partial<Record<Track['kind'], string>>;
}

export function addVideoClip(edit: Edit, args: AddVideoClipArgs): Produced {
  const track = resolveTargetTrack(edit, 'video', args.targetTrack);
  if (!track || track.kind !== 'video') return NOTHING;
  const srcId = ensureSource(edit, args.source);
  const end = trackEnd(track);
  const start = args.at !== undefined && args.at > end ? args.at : end;
  const id = genId('c');
  track.clips.push(newVideoClip(id, srcId, args.in, args.out, start));
  return { created: [id] };
}

export interface InsertEditClipsArgs {
  clips: { source: PandoraVideoSource; in: number; out: number }[];
  at: number;
  /** Explicit destination track id; falls back to `targetTrack` resolution. */
  trackId?: string;
  targetTrack?: Partial<Record<Track['kind'], string>>;
}

/**
 * Ripple-insert a sequence of video clips end-to-end at `at`, pushing existing
 * clips right. A clip straddling `at` is not split: the sequence lands at that
 * clip's end instead (see the comments below).
 */
export function insertEditClips(edit: Edit, args: InsertEditClipsArgs): Produced {
  if (!args.clips.length) return NOTHING;
  const insertAt = Math.max(0, args.at);
  const targeted = args.trackId
    ? edit.tracks.find((t) => t.id === args.trackId && t.kind === 'video')
    : undefined;
  const track = targeted ?? resolveTargetTrack(edit, 'video', args.targetTrack);
  if (!track || track.kind !== 'video') return NOTHING;
  const total = args.clips.reduce((sum, c) => sum + (c.out - c.in), 0);
  // If the playhead sits inside a clip, land the sequence at that clip's
  // end rather than mid-clip: this keeps clips whole and avoids overlaps
  // that normalizeVideoTrack would silently turn into crossfades.
  const straddler = track.clips.find(
    (c) => c.start < insertAt - 1e-6 && clipEnd(c) > insertAt + 1e-6,
  );
  const at = straddler ? clipEnd(straddler) : insertAt;
  // Push everything at/after the insertion point right by exactly the
  // inserted duration — neighbours abut without manufacturing a crossfade.
  rippleLaterClips(track, at, total);
  const created: string[] = [];
  let cursor = at;
  for (const { source, in: inPoint, out: outPoint } of args.clips) {
    const srcId = ensureSource(edit, source);
    const id = genId('c');
    track.clips.push(newVideoClip(id, srcId, inPoint, outPoint, cursor));
    created.push(id);
    cursor += outPoint - inPoint;
  }
  return { created };
}

/**
 * Ripple-move a clip to a new timeline position: close the gap it leaves and
 * push the destination open by the same duration.
 *
 * The naive alternative — just setting `start` — is what `updateClip` does, and
 * it is wrong for reordering: moving a clip backwards leaves it overlapping its
 * new neighbour, and `normalizeVideoTrack` silently converts any overlap into a
 * crossfade. Asking to move the third clip to the front produced two clips
 * stacked on top of each other joined by a 10s crossfade nobody asked for.
 */
/**
 * Shift every clip from `index` onward by `delta`, by POSITION rather than by
 * time.
 *
 * `rippleLaterClips` selects on `start >= fromTime`, which is right when the
 * caller has a time (a playhead) and wrong when it has an anchor clip. Two
 * clips joined by a transition overlap by the transition's duration, so the
 * clip *after* the anchor starts BEFORE the anchor ends — a time-based ripple
 * skips it entirely, and the new clip lands on top of it.
 */
function rippleFromIndex(track: Track, index: number, delta: number): void {
  const clips = track.clips as AnyClip[];
  for (let i = index; i < clips.length; i++) {
    clips[i].start = Math.max(0, clips[i].start + delta);
  }
}

/** Index of a clip on its track, or -1. */
function indexOfClip(track: Track, clipId: string): number {
  return (track.clips as AnyClip[]).findIndex((c) => c.id === clipId);
}

export interface InsertRelativeArgs {
  clips: { source: PandoraVideoSource; in: number; out: number }[];
  /** Insert directly after this clip. */
  after?: string;
  /** Insert directly before this clip. */
  before?: string;
}

/**
 * Insert clips at a position stated relative to another CLIP, not a time.
 *
 * Deliberately does NOT use the straddle rule that `insertEditClips` applies to
 * a raw time: that rule disambiguates "the playhead is inside a clip", and an
 * explicit anchor has nothing to disambiguate. Applying it turned "after the
 * first clip" into "after the second" whenever the two overlapped for a
 * crossfade.
 *
 * An existing transition at the insertion point is preserved by construction:
 * the tail ripples by exactly the inserted duration, so whatever overlap
 * existed now sits between the last inserted clip and the old follower.
 */
export function insertClipsRelative(edit: Edit, args: InsertRelativeArgs): Produced {
  if (!args.clips.length) return NOTHING;
  const anchorId = args.after ?? args.before;
  if (anchorId === undefined) return NOTHING;

  const loc = findClip(edit, anchorId);
  if (!loc || loc.track.kind !== 'video') return NOTHING;
  const track = loc.track;
  const clips = track.clips;

  const anchorIndex = indexOfClip(track, anchorId);
  if (anchorIndex < 0) return NOTHING;

  // "after A" and "before B" are the same operation at different indices.
  const insertIndex = args.after !== undefined ? anchorIndex + 1 : anchorIndex;
  const previous = clips[insertIndex - 1];
  const insertAt = previous ? clipEnd(previous) : 0;

  const total = args.clips.reduce((sum, c) => sum + (c.out - c.in), 0);
  rippleFromIndex(track, insertIndex, total);

  const created: string[] = [];
  let cursor = insertAt;
  for (const { source, in: inPoint, out: outPoint } of args.clips) {
    const srcId = ensureSource(edit, source);
    const id = genId('c');
    clips.push(newVideoClip(id, srcId, inPoint, outPoint, cursor));
    created.push(id);
    cursor += outPoint - inPoint;
  }
  return { created };
}

export interface MoveClipArgs {
  clip: string;
  /** Land directly after this clip. */
  after?: string;
  /** Land directly before this clip. */
  before?: string;
  /** Land at this timeline time. Used only when after/before are absent. */
  at?: number;
}

export function moveClipRipple(edit: Edit, args: MoveClipArgs): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc) return NOTHING;
  const track = loc.track;
  const clips = track.clips as AnyClip[];
  const index = indexOfClip(track, args.clip);
  if (index < 0) return NOTHING;

  const clip = clips[index];
  const duration = clipDuration(clip);

  // Lift it out and close the gap behind it FIRST, by index for the same reason
  // as insertClipsRelative: a time-based ripple skips a follower that overlaps
  // the moved clip for a transition. The destination is then resolved against
  // the timeline as it will actually be — an anchor resolved beforehand is
  // stale by exactly the lifted duration and leaves a hole behind.
  // Snapshot enough to undo the lift EXACTLY. `rippleFromIndex` clamps at 0, so
  // shifting the tail left and then right again is not an inverse whenever a
  // clip would cross zero: restoring by re-rippling silently relocated
  // neighbours and dropped a transition. The abort path must put back what was
  // there, not try to recompute it.
  const startsBefore = clips.map((c) => c.start);
  const transitionBefore = 'transitionIn' in clip ? (clip as VideoClip).transitionIn : undefined;
  const restore = (): Produced => {
    clips.splice(index, 0, clip as never);
    clips.forEach((c, i) => {
      c.start = startsBefore[i];
    });
    if (transitionBefore !== undefined) (clip as VideoClip).transitionIn = transitionBefore;
    return NOTHING;
  };

  clips.splice(index, 1);
  if ('transitionIn' in clip) (clip as VideoClip).transitionIn = null;
  rippleFromIndex(track, index, -duration);

  const anchorId = args.after ?? args.before;
  let insertIndex: number;
  if (anchorId !== undefined) {
    const anchorIndex = indexOfClip(track, anchorId);
    if (anchorIndex < 0) {
      // Anchor is not on this track, or vanished mid-replay. Put the timeline
      // back exactly as it was rather than leaving it disturbed.
      return restore();
    }
    insertIndex = args.after !== undefined ? anchorIndex + 1 : anchorIndex;
  } else {
    // An explicit time: land at the first position at/after it.
    const to = Math.max(0, args.at ?? clip.start);
    const found = clips.findIndex((c) => c.start >= to - 1e-6);
    insertIndex = found < 0 ? clips.length : found;
  }

  const previous = clips[insertIndex - 1];
  const insertAt = previous ? clipEnd(previous) : 0;
  rippleFromIndex(track, insertIndex, duration);
  clip.start = insertAt;
  clips.splice(insertIndex, 0, clip as never);
  return NOTHING;
}

export interface AddAudioClipArgs {
  source: PandoraVideoSource;
  in: number;
  out: number;
  at?: number;
  targetTrack?: Partial<Record<Track['kind'], string>>;
}

export function addAudioClip(edit: Edit, args: AddAudioClipArgs): Produced {
  const track = resolveTargetTrack(edit, 'audio', args.targetTrack);
  if (!track || track.kind !== 'audio') return NOTHING;
  const srcId = ensureSource(edit, args.source);
  const end = trackEnd(track);
  const start = args.at !== undefined && args.at > end ? args.at : end;
  const id = genId('a');
  track.clips.push({
    id,
    source: srcId,
    in: args.in,
    out: args.out,
    start,
    speed: 1,
    gain: 1,
    fadeIn: 0,
    fadeOut: 0,
  });
  return { created: [id] };
}

export interface AddTextClipArgs {
  text: string;
  /** Requested timeline start (s); resolved to the next free overlay slot. */
  start: number;
  duration: number;
  style?: Partial<TextStyle>;
  position?: { x: number; y: number };
  fadeIn?: number;
  fadeOut?: number;
  targetTrack?: Partial<Record<Track['kind'], string>>;
}

export function addTextClip(edit: Edit, args: AddTextClipArgs): Produced {
  const track = resolveTargetTrack(edit, 'overlay', args.targetTrack);
  if (!track || track.kind !== 'overlay') return NOTHING;
  // Overlay clips can't overlap: place at the next free slot at/after the
  // requested start rather than let normalization shove it to the end.
  const start = findFreeOverlayStart(
    overlayIntervals(edit),
    Math.max(0, args.start),
    args.duration,
  );
  const id = genId('t');
  track.clips.push({
    id,
    kind: 'text',
    text: args.text,
    start,
    duration: args.duration,
    style: { ...DEFAULT_TEXT_STYLE, ...args.style },
    position: args.position ?? { x: 0.5, y: 0.85 },
    fadeIn: args.fadeIn ?? 0.3,
    fadeOut: args.fadeOut ?? 0.3,
  });
  return { created: [id] };
}

export function deleteClips(edit: Edit, args: { clips: string[] }): Produced {
  if (!args.clips.length) return NOTHING;
  const idSet = new Set(args.clips);
  for (const track of edit.tracks) {
    track.clips = (track.clips as AnyClip[]).filter((c) => !idSet.has(c.id)) as never;
  }
  return NOTHING;
}

/** Delete clips and close the freed gaps on each affected track. */
export function rippleDelete(edit: Edit, args: { clips: string[] }): Produced {
  if (!args.clips.length) return NOTHING;
  const idSet = new Set(args.clips);
  for (const track of edit.tracks) {
    const clips = track.clips as AnyClip[];
    const deleted = clips
      .filter((c) => idSet.has(c.id))
      .map((c) => ({ start: c.start, duration: clipDuration(c) }))
      .sort((a, b) => a.start - b.start);
    if (!deleted.length) continue;
    track.clips = clips.filter((c) => !idSet.has(c.id)) as never;
    // Close each freed gap from right to left, so closing a left gap does
    // not disturb the fixed left-edge of gaps not yet closed. Shift by
    // exactly the freed duration → neighbours abut without overlapping
    // (which would make normalizeVideoTrack manufacture a crossfade).
    for (let i = deleted.length - 1; i >= 0; i--) {
      rippleLaterClips(track, deleted[i].start, -deleted[i].duration);
    }
  }
  return NOTHING;
}

export interface PasteClipsArgs {
  /** The copied clips, verbatim (already detached from any live edit). */
  clips: AnyClip[];
  /** Sources the copied clips reference, by their id in the source edit. */
  sources: Record<string, Source>;
  /** Timeline time the earliest pasted clip lands at. */
  at: number;
}

/**
 * Paste clips at `at`, preserving their relative offsets. Sources are remapped
 * into this edit (dedup by identity, so pasting within one edit adds nothing).
 * Clipboard ownership stays in the store; this op takes the payload as args.
 */
export function pasteClips(edit: Edit, args: PasteClipsArgs): Produced {
  if (!args.clips.length) return NOTHING;
  const minStart = Math.min(...args.clips.map((c) => c.start));
  const offset = args.at - minStart;
  const created: string[] = [];
  for (const orig of args.clips) {
    const clip = structuredClone(orig) as AnyClip;
    // remap the source (dedupes across edits; adds missing image sources)
    if ('source' in clip) {
      const oldId = (clip as { source: string }).source;
      const src = args.sources[oldId];
      if (src) (clip as { source: string }).source = ensureAnySource(edit, src);
    }
    const relStart = Math.max(0, clip.start + offset);
    if (isOverlayClip(clip)) {
      clip.id = genId('t');
      clip.start = findFreeOverlayStart(overlayIntervals(edit), relStart, clipDuration(clip));
      const track = firstTrackOfKind(edit, 'overlay');
      if (track) (track.clips as AnyClip[]).push(clip as never);
    } else if (isAudioClip(clip)) {
      clip.id = genId('a');
      clip.start = relStart;
      const track = firstTrackOfKind(edit, 'audio');
      if (track) (track.clips as AnyClip[]).push(clip as never);
    } else {
      const v = clip as VideoClip;
      v.id = genId('c');
      v.start = relStart;
      v.transitionIn = null; // pasted clip has no left neighbour to cross into
      v.effects = reassignEffectIds(v.effects); // never share effect ids across clips
      const track = firstTrackOfKind(edit, 'video');
      if (track) (track.clips as AnyClip[]).push(clip as never);
    }
    created.push(clip.id);
  }
  return { created };
}

/** Split each of `clips` at timeline time `at`. Created ids are the right halves. */
export function splitAt(edit: Edit, args: { at: number; clips: string[] }): Produced {
  const created: string[] = [];
  for (const id of args.clips) {
    const loc = findClip(edit, id);
    if (!loc) continue;
    if (loc.track.kind === 'video') {
      const nid = splitVideoClip(loc.track, id, args.at);
      if (nid) created.push(nid);
    } else if (loc.track.kind === 'audio') {
      const nid = splitAudioClip(loc.track, id, args.at);
      if (nid) created.push(nid);
    }
  }
  return { created };
}

export interface DetachAudioArgs {
  /** Video clip ids. Non-video and already-muted ids are skipped. */
  clips: string[];
  targetTrack?: Partial<Record<Track['kind'], string>>;
}

/**
 * Audio track with room for `[start, end)`, creating one when every existing
 * track is occupied there. Preferring a free track over pushing clips around is
 * what lets a detached crossfade pair keep its exact timing: the two halves
 * overlap, audio tracks may not, so the second one needs a lane of its own.
 */
function audioTrackWithRoom(
  edit: Edit,
  start: number,
  end: number,
  target: Partial<Record<Track['kind'], string>> | undefined,
): AudioTrack | null {
  const preferred = resolveTargetTrack(edit, 'audio', target);
  const candidates = [
    ...(preferred && preferred.kind === 'audio' ? [preferred] : []),
    ...edit.tracks.filter((t): t is AudioTrack => t.kind === 'audio' && t !== preferred),
  ];
  for (const t of candidates) {
    if (trackHasRoom(t, start, end)) return t;
  }
  const { created } = addTrack(edit, {
    kind: 'audio',
    afterTrackId: candidates[candidates.length - 1]?.id,
  });
  return (edit.tracks.find((t) => t.id === created[0]) as AudioTrack | undefined) ?? null;
}

/**
 * Move each video clip's audio onto an audio track and mute the video clip.
 * Created ids are the new audio clips, in the order `args.clips` gave them.
 *
 * `volume` is left on the video clip so unmuting restores the original level —
 * detaching is meant to be undoable by hand, not just by Cmd+Z.
 */
export function detachAudio(edit: Edit, args: DetachAudioArgs): Produced {
  const created: string[] = [];
  // video clip id -> the audio clip detached from it, for the fade pass below
  const detached = new Map<string, AudioClip>();
  for (const id of args.clips) {
    const loc = findClip(edit, id);
    if (!loc || loc.track.kind !== 'video') continue;
    const clip = loc.clip as VideoClip;
    if (clip.muted) continue; // nothing audible to detach

    const audio: AudioClip = {
      id: genId('a'),
      source: clip.source,
      in: clip.in,
      out: clip.out,
      start: clip.start,
      speed: clip.speed,
      gain: clip.volume,
      fadeIn: clip.fadeIn,
      fadeOut: clip.fadeOut,
    };

    // Audio tracks have no transition machinery (audioClipGainAt has no
    // equal-power branch), so approximate the crossfade this clip sat under
    // with matching fades on both sides of it.
    const transition = clip.transitionIn;
    if (transition) {
      audio.fadeIn = Math.max(audio.fadeIn, transition.duration);
      const clips = loc.track.clips as VideoClip[];
      const prev = clips[clips.findIndex((c) => c.id === id) - 1];
      const prevAudio = prev ? detached.get(prev.id) : undefined;
      if (prevAudio) prevAudio.fadeOut = Math.max(prevAudio.fadeOut, transition.duration);
    }

    const track = audioTrackWithRoom(
      edit,
      audio.start,
      audio.start + clipDuration(audio),
      args.targetTrack,
    );
    if (!track) continue;
    track.clips.push(audio);
    clip.muted = true;
    detached.set(id, audio);
    created.push(audio.id);
  }
  return { created };
}

export interface UpdateClipArgs {
  clip: string;
  patch: Partial<VideoClip> | Partial<TextClip> | Partial<AudioClip>;
}

export function updateClip(edit: Edit, args: UpdateClipArgs): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc) return NOTHING;
  Object.assign(loc.clip, args.patch);
  return NOTHING;
}

export interface SetTransitionArgs {
  clip: string;
  type: TransitionType;
  duration: number;
}

export function setClipTransition(edit: Edit, args: SetTransitionArgs): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc || loc.track.kind !== 'video') return NOTHING;
  setTransition(loc.track, args.clip, args.type, args.duration);
  return NOTHING;
}

export function removeClipTransition(edit: Edit, args: { clip: string }): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc || loc.track.kind !== 'video') return NOTHING;
  removeTransition(loc.track, args.clip);
  return NOTHING;
}

export function moveClipToTrack(edit: Edit, args: { clip: string; track: string }): Produced {
  moveClipToTrackInvariant(edit, args.clip, args.track);
  return NOTHING;
}

// ---------------------------------------------------------------------------
// project settings
// ---------------------------------------------------------------------------

export function setSettings(edit: Edit, args: { patch: Partial<EditSettings> }): Produced {
  Object.assign(edit.settings, args.patch);
  return NOTHING;
}

// ---------------------------------------------------------------------------
// track ops
// ---------------------------------------------------------------------------

const TRACK_LABEL_PREFIX: Record<Track['kind'], string> = {
  video: 'V',
  overlay: 'T',
  audio: 'A',
};

/** Lowest unused `V1`/`T1`/`A1`-style label for a new track of this kind. */
export function nextTrackLabel(edit: Edit, kind: Track['kind']): string {
  const prefix = TRACK_LABEL_PREFIX[kind];
  const used = new Set(edit.tracks.filter((t) => t.kind === kind).map((t) => t.label));
  for (let n = 1; ; n++) {
    const label = `${prefix}${n}`;
    if (!used.has(label)) return label;
  }
}

export interface AddTrackArgs {
  kind: Track['kind'];
  /** Insert directly after this track when it is of the same kind. */
  afterTrackId?: string;
}

/**
 * Add a track, keeping same-kind tracks contiguous (video draw order is array
 * order, so the blocks must not interleave).
 */
export function addTrack(edit: Edit, args: AddTrackArgs): Produced {
  const { kind } = args;
  const id = genId('trk');
  const label = nextTrackLabel(edit, kind);
  const base = { id, label, muted: false };
  const track: Track =
    kind === 'video'
      ? { ...base, kind, clips: [] }
      : kind === 'overlay'
        ? { ...base, kind, clips: [] }
        : { ...base, kind, gain: 1.0, clips: [] };
  // insert after the anchor when it is the same kind, else after the last
  // track of this kind
  let at = -1;
  if (args.afterTrackId) {
    const i = edit.tracks.findIndex((t) => t.id === args.afterTrackId && t.kind === kind);
    if (i >= 0) at = i + 1;
  }
  if (at < 0) {
    for (let i = edit.tracks.length - 1; i >= 0; i--) {
      if (edit.tracks[i].kind === kind) {
        at = i + 1;
        break;
      }
    }
  }
  if (at < 0) at = edit.tracks.length;
  edit.tracks.splice(at, 0, track);
  return { created: [id] };
}

export function removeTrack(edit: Edit, args: { track: string }): Produced {
  edit.tracks = edit.tracks.filter((t) => t.id !== args.track);
  return NOTHING;
}

/** Reorder within the same-kind block only; a cross-kind swap is a no-op. */
export function moveTrack(edit: Edit, args: { track: string; dir: 1 | -1 }): Produced {
  const i = edit.tracks.findIndex((t) => t.id === args.track);
  if (i < 0) return NOTHING;
  const j = i + args.dir;
  if (j < 0 || j >= edit.tracks.length) return NOTHING;
  if (edit.tracks[j].kind !== edit.tracks[i].kind) return NOTHING;
  [edit.tracks[i], edit.tracks[j]] = [edit.tracks[j], edit.tracks[i]];
  return NOTHING;
}

export function renameTrack(edit: Edit, args: { track: string; label: string }): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track) track.label = args.label;
  return NOTHING;
}

export function setTrackMuted(edit: Edit, args: { track: string; muted: boolean }): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track) track.muted = args.muted;
  return NOTHING;
}

export function setTrackSolo(edit: Edit, args: { track: string; solo: boolean }): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track) track.solo = args.solo;
  return NOTHING;
}

export function setTrackGain(edit: Edit, args: { track: string; gain: number }): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track && track.kind === 'audio') track.gain = Math.max(0, Math.min(2, args.gain));
  return NOTHING;
}

export function setTrackBlend(
  edit: Edit,
  args: { track: string; blend: TrackBlend | null },
): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track && track.kind === 'video') {
    if (args.blend) track.blend = args.blend;
    else delete track.blend;
  }
  return NOTHING;
}

export function setTrackOpacity(edit: Edit, args: { track: string; opacity: number }): Produced {
  const track = edit.tracks.find((t) => t.id === args.track);
  if (track && track.kind === 'video') track.opacity = Math.max(0, Math.min(1, args.opacity));
  return NOTHING;
}

// ---------------------------------------------------------------------------
// keyframe ops
// ---------------------------------------------------------------------------

/**
 * `param` addresses a Param on the clip ('transform.scale', 'effects.<id>.amount',
 * 'position.x'). `atTimelineT` is TIMELINE time (usually the playhead) —
 * converted here to the clip's own keyframe axis (source time for media clips,
 * clip-relative for overlays), which is what callers upstream never have to know.
 */
export interface ParamKeyframeArgs {
  clip: string;
  param: string;
  atTimelineT: number;
}

export function setParamKeyframe(
  edit: Edit,
  args: ParamKeyframeArgs & { value: number },
): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc) return NOTHING;
  const p = getParamAtPath(loc.clip, args.param);
  if (p === null) return NOTHING;
  const t = clipParamTime(loc.clip, args.atTimelineT);
  setParamAtPath(loc.clip, args.param, setParamAt(p, t, args.value));
  return NOTHING;
}

/** static → animated (1 keyframe at the given time); animated → static (bakes the sampled value). */
export function toggleParamAnimated(edit: Edit, args: ParamKeyframeArgs): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc) return NOTHING;
  const p = getParamAtPath(loc.clip, args.param);
  if (p === null) return NOTHING;
  const t = clipParamTime(loc.clip, args.atTimelineT);
  if (isAnimated(p)) {
    setParamAtPath(loc.clip, args.param, sampleParam(p, t)); // bake
  } else {
    setParamAtPath(loc.clip, args.param, setParamAt(p, t, p));
  }
  return NOTHING;
}

export function removeParamKeyframe(edit: Edit, args: ParamKeyframeArgs): Produced {
  const loc = findClip(edit, args.clip);
  if (!loc) return NOTHING;
  const p = getParamAtPath(loc.clip, args.param);
  if (p === null || !isAnimated(p)) return NOTHING;
  const t = clipParamTime(loc.clip, args.atTimelineT);
  setParamAtPath(loc.clip, args.param, removeKeyframeAt(p, t, 1 / 60));
  return NOTHING;
}
